import styled, { css } from 'styled-components';
import { ColorName, colors } from '@karnott/colors';
import { fontFamily, msDuration, pixelSize } from '@karnott/theme';

export const TableContainer = styled.div`
  height: 100%;
  overflow: auto;
  border-radius: 7px;
  background-color: ${colors('white')};
  overscroll-behavior-x: none;
`;

export const TableElement = styled.table<{ fixedLayout?: boolean }>`
  border-collapse: collapse;
  border-spacing: 0;
  width: 100%;
  font-family: ${fontFamily('default')};
  ${({ fixedLayout }) =>
    fixedLayout
      ? css`
          table-layout: fixed;
        `
      : null}
`;

export const Thead = styled.thead<{
  background?: string;
}>`
  background: ${({ background }) =>
    background
      ? background in colors._palette
        ? colors(background as ColorName, 500)
        : background
      : colors('grey', 200)};
  font-family: ${fontFamily('overstatement')};
  color: ${({ background }) => (background ? colors('white') : colors('grey', 600))};
  text-transform: uppercase;
  margin: 0;
  position: sticky;
  top: 0;
  z-index: 1;
`;

export const HeaderCell = styled.td<{
  canSort: boolean;
  background?: string;
  $width?: string;
}>`
  font-weight: normal;
  font-size: ${pixelSize('small')};
  padding: 0 10px;
  min-width: 50px;

  ${({ canSort, background }) =>
    canSort
      ? css`
          &:hover {
            color: ${background ? 'inherit' : colors('grey', 700)};
            cursor: pointer;
          }
        `
      : null}
  ${({ $width }) =>
    $width
      ? css`
          width: ${$width};
        `
      : null}

  &:has(.cell_no_data) {
    min-width: 0;
  }
`;

export const HeaderWithSort = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  &:has(.cell_no_data) {
    min-width: 0;
    justify-content: center;
  }
`;

export const HeaderTitle = styled.span<{
  multiline: boolean;
}>`
  user-select: none;
  padding: 14px 0;
  ${({ multiline }) =>
    multiline
      ? null
      : css`
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        `}

  &:empty {
    display: none;
  }
`;

export const SortButtons = styled.div`
  flex-shrink: 0;

  > div:first-of-type {
    rotate: 0.5turn;
    translate: 0 4px;
  }

  > div:last-of-type {
    translate: 0 -4px;
  }
`;

export const Tbody = styled.tbody`
  background-color: ${colors('white')};
  color: ${colors('black')};
  font-size: ${pixelSize('regular')};

  tr:last-of-type td {
    border-bottom: none;
  }
`;

export const TableRow = styled.tr<{
  highlightOnHover: boolean;
}>`
  background-color: ${colors('white')};
  transition:
    background-color ${msDuration('short')} linear,
    box-shadow ${msDuration('short')} ease-out;

  .cell_on_hover {
    opacity: 0;
    transition: opacity ${msDuration('short')} linear;
  }

  &:hover .cell_on_hover {
    opacity: 1;
  }

  &[aria-disabled='false'] {
    cursor: grab;
  }

  &[aria-grabbed='true'] {
    background-color: ${colors('green', 200)};
    outline: none;
    box-shadow: rgba(149, 157, 165, 0.2) 0px 8px 24px;

    td {
      border-bottom-color: ${colors('green', 200)};
    }
  }

  ${({ highlightOnHover }) =>
    highlightOnHover
      ? css`
          &:hover {
            background-color: ${colors('green', 200)};
            td {
              border-bottom-color: ${colors('green', 200)};
            }
          }

          &:has(+ :hover) td {
            border-bottom-color: ${colors('green', 200)};
          }
        `
      : null}
`;

export const BodyCell = styled.td`
  position: relative;
  padding: 14px 10px;
  border-bottom: 1px solid ${colors('grey', 100)};
  transition: border-bottom-color ${msDuration('short')} linear;

  .cell_no_data {
    display: flex;
    justify-content: center;
    align-items: center;
  }
`;

export const Tfoot = styled.tfoot`
  background: ${colors('black')};
  color: ${colors('white')};
  margin: 0;
  position: sticky;
  bottom: 0;
  font-size: ${pixelSize('regular')};

  th {
    font-weight: normal;
    text-align: left;
  }
`;

export const FooterCell = styled.td`
  margin: 0;
  padding: 14px 10px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  * {
    color: ${colors('white')} !important;
    font-family: ${fontFamily()} !important;
    font-weight: normal !important;
  }
`;
